"use client";

import React from "react";

import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select";

// Define the structure for the date value
export type DateValue = {
  year: number | "";
  month: number | "";
  day: number | "";
};

export interface CalendarPickerProps<T extends FieldValues = FieldValues> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  control?: Control<T>;
  name: Path<T>;
  label?: string;
  minYear?: number;
  maxYear?: number;
  dayLabel?: string;
  monthLabel?: string;
  yearLabel?: string;
  className?: string;
  selectClassName?: string;
  errorClassName?: string;
  monthLabels?: string[];
  disableFutureDates?: boolean;
  markEmptyAsError?: boolean;
  testId?: string;
}

export const CalendarPicker = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  dayLabel = "Day",
  monthLabel = "Month",
  yearLabel = "Year",
  className,
  selectClassName = "p-2", // Default styling
  errorClassName = "text-red-600 text-xs mt-1", // Default error styling
  monthLabels,
  disableFutureDates = false,
  markEmptyAsError = false,
  testId,
}: CalendarPickerProps<T>) => {
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController<T, Path<T>>({
    name,
    control,
  });

  // Internal state for individual select values, derived from `value`
  const [internalDay, setInternalDay] = React.useState<number | "">(value?.day || "");
  const [internalMonth, setInternalMonth] = React.useState<number | "">(value?.month || "");
  const [internalYear, setInternalYear] = React.useState<number | "">(value?.year || "");

  const yearOptions = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  // Current date info for future date restrictions
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() is zero-indexed
  const currentDay = currentDate.getDate();

  const monthOptionsFull = (
    monthLabels || [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ]
  ).map((label, index) => ({ value: index + 1, label }));

  // If disableFutureDates is true and the selected year is current,
  // only show months up to the current month.
  const monthOptions =
    disableFutureDates && Number(internalYear) === currentYear
      ? monthOptionsFull.filter((m) => m.value <= currentMonth)
      : monthOptionsFull;

  // Day options based on the selected month and year.
  // Also, if disableFutureDates is true and the selected this year and month
  //limit days
  const totalDaysInMonth =
    internalMonth && internalYear
      ? new Date(Number(internalYear), Number(internalMonth), 0).getDate()
      : 31;
  const fullDayOptions = Array.from({ length: totalDaysInMonth }, (_, i) => i + 1);
  const dayOptions =
    disableFutureDates &&
    Number(internalYear) === currentYear &&
    Number(internalMonth) === currentMonth
      ? fullDayOptions.filter((d) => d <= currentDay)
      : fullDayOptions;

  const handleSelectChange = (type: "year" | "month" | "day", selectedValue: string) => {
    const numericValue = Number(selectedValue) || "";

    const newDate: DateValue = {
      ...value,
      [type]: numericValue,
    };

    if (type === "year") {
      setInternalYear(numericValue);
    } else if (type === "month") {
      setInternalMonth(numericValue);
    } else if (type === "day") {
      setInternalDay(numericValue);
    }

    onChange(newDate);
  };

  return (
    <div className={className} data-testid={testId}>
      {label && (
        <label htmlFor={name as string} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="flex gap-2">
        {/* Day */}
        <div className="flex-1">
          <label htmlFor={`${name}-day`} className="sr-only">
            {dayLabel}
          </label>
          <Select
            value={internalDay ? String(internalDay) : ""}
            onValueChange={(value) => handleSelectChange("day", value)}
            onOpenChange={() => onBlur()}
          >
            <SelectTrigger
              id={`${name}-day`}
              className={`[&>svg]:!text-brand-blue w-full [&>svg]:!opacity-100 ${
                markEmptyAsError && !internalDay
                  ? "border border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                  : ""
              } ${selectClassName}`}
              ref={ref}
            >
              <SelectValue placeholder={dayLabel} />
            </SelectTrigger>
            <SelectContent className="md:max-h-64">
              <SelectGroup>
                {dayOptions.map((d) => (
                  <SelectItem
                    className="text-brand-black-30 data-[highlighted]:bg-brand-turquoise w-full justify-center rounded-none data-[highlighted]:text-white"
                    key={d}
                    value={String(d)}
                  >
                    {d}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Month */}
        <div className="flex-1">
          <label htmlFor={`${name}-month`} className="sr-only">
            {monthLabel}
          </label>
          <Select
            value={internalMonth ? String(internalMonth) : ""}
            onValueChange={(value) => handleSelectChange("month", value)}
            onOpenChange={() => onBlur()}
          >
            <SelectTrigger
              id={`${name}-month`}
              className={`[&>svg]:!text-brand-blue w-full [&>svg]:!opacity-100 ${
                markEmptyAsError && !internalMonth
                  ? "border border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                  : ""
              } ${selectClassName}`}
            >
              <SelectValue placeholder={monthLabel} />
            </SelectTrigger>
            <SelectContent className="md:max-h-64">
              <SelectGroup>
                {monthOptions.map((m) => (
                  <SelectItem
                    className="text-brand-black-30 data-[highlighted]:bg-brand-turquoise w-full justify-center rounded-none data-[highlighted]:text-white"
                    key={m.value}
                    value={String(m.value)}
                  >
                    {m.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Year */}
        <div className="flex-1">
          <label htmlFor={`${name}-year`} className="sr-only">
            {yearLabel}
          </label>
          <Select
            value={internalYear ? String(internalYear) : ""}
            onValueChange={(value) => handleSelectChange("year", value)}
            onOpenChange={() => onBlur()}
          >
            <SelectTrigger
              id={`${name}-year`}
              className={`[&>svg]:!text-brand-blue w-full [&>svg]:!opacity-100 ${
                markEmptyAsError && !internalYear
                  ? "border border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                  : ""
              } ${selectClassName}`}
            >
              <SelectValue placeholder={yearLabel} />
            </SelectTrigger>
            <SelectContent className="md:max-h-64">
              <SelectGroup>
                {yearOptions.map((y) => (
                  <SelectItem
                    className="text-brand-black-30 data-[highlighted]:bg-brand-turquoise w-full justify-center rounded-none data-[highlighted]:text-white"
                    key={y}
                    value={String(y)}
                  >
                    {y}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      {error && <p className={errorClassName}>{error.message}</p>}
    </div>
  );
};

export default CalendarPicker;
